import { IonIcon, IonItem, IonLabel, IonList, IonText } from '@ionic/react';
import { eyeOutline, lockClosedOutline, shieldOutline } from 'ionicons/icons';
import { useMemo } from 'react';
import { useComponentTranslation } from '../../hooks/useTranslation';
import { formatDay, formatHour, formatMonth, userLocale, weekdayNames } from '../../utils/statsFormat';
import type { CatActivitySummary, MonthlyTransits } from '../../utils/activityStatsSummary';
import StatBars from './StatBars';
import StatTile from './StatTile';
import styles from './stats.module.css';

/**
 * The shared 30-day summary cards (design: Pet Profile Treatment A), fed by
 * summarizeCatActivity output. Copy lives in the components namespace under
 * `stats.*` so the pet profile, household stats and device stats screens
 * stay worded identically.
 */

export const PreyCard: React.FC<{
    preyAttempts: number;
    lastPreyDay: string | null;
    daysSinceLastPrey: number | null;
    /** Overrides the "no contraband" sub line (e.g. device-scoped copy). */
    noPreyText?: string;
}> = ({ preyAttempts, lastPreyDay, daysSinceLastPrey, noPreyText }) => {
    const { t } = useComponentTranslation();
    return (
        <div className={`${styles.card} ${styles.preyCard}`}>
            <span className={styles.preyIcon}><IonIcon icon={shieldOutline} /></span>
            <div className={styles.preyBody}>
                <div className={styles.preyHeadline}>
                    <span className={styles.bigNumber}>{preyAttempts}</span>
                    <span className={styles.preyLabel}>{t('stats.contraband_detected', { count: preyAttempts })}</span>
                </div>
                <div className={styles.mutedSmall}>
                    {lastPreyDay && daysSinceLastPrey !== null
                        ? t('stats.last_prey', {
                            date: formatDay(lastPreyDay, { day: 'numeric', month: 'short' }),
                            count: daysSinceLastPrey
                        })
                        : noPreyText ?? t('stats.no_contraband')}
                </div>
            </div>
        </div>
    );
};

export const TransitsCard: React.FC<{
    transits: number;
    transitsIn: number;
    transitsOut: number;
    trendPct: number | null;
}> = ({ transits, transitsIn, transitsOut, trendPct }) => {
    const { t } = useComponentTranslation();
    return (
        <div className={`${styles.card} ${styles.transitsCard}`}>
            <div className={styles.transitsHeadline}>
                <span className={styles.bigNumber}>{transits}</span>
                <span className={styles.mutedSmall}>{t('stats.transits', { count: transits })}</span>
                {trendPct !== null && (
                    <span className={`${styles.trend} ${trendPct >= 0 ? styles.trendUp : ''}`}>
                        {t('stats.vs_previous_month', { trend: `${trendPct >= 0 ? '+' : ''}${trendPct}%` })}
                    </span>
                )}
            </div>
            {transits > 0 && (
                <div className={styles.inOutSplit}>
                    <div style={{ flex: Math.max(transitsOut, 1) }}>
                        <div className={`${styles.inOutBar} ${styles.inOutBarOut}`} />
                        <div className={styles.mutedSmall}>
                            {t('stats.went_out')} <strong>{transitsOut}</strong>
                        </div>
                    </div>
                    <div style={{ flex: Math.max(transitsIn, 1) }}>
                        <div className={`${styles.inOutBar} ${styles.inOutBarIn}`} />
                        <div className={styles.mutedSmall}>
                            {t('stats.came_in')} <strong>{transitsIn}</strong>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/** The denies + peeks tile pair under the transit card. */
export const DenyPeekTiles: React.FC<{ denies: number; peeks: number }> = ({ denies, peeks }) => {
    const { t } = useComponentTranslation();
    return (
        <div className={styles.tileGrid}>
            <StatTile
                value={denies}
                label={t('stats.denies_label')}
                sub={t('stats.denies_sub')}
                icon={lockClosedOutline}
            />
            <StatTile
                value={peeks}
                label={t('stats.peeks_label')}
                sub={t('stats.peeks_sub')}
                icon={eyeOutline}
            />
        </div>
    );
};

/** Hourly transit histogram card with busiest-hour headline and core-hours note. */
export const HourHistogramCard: React.FC<{
    hourHistogram: number[];
    busiestHour: number | null;
    coreHours: CatActivitySummary['coreHours'];
}> = ({ hourHistogram, busiestHour, coreHours }) => {
    const { t } = useComponentTranslation();
    const hourCategories = useMemo(() => Array.from({ length: 24 }, (_, hour) => formatHour(hour)), []);
    return (
        <div className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.chartHeadline}>
                <span className={styles.chartTitle}>{busiestHour !== null ? formatHour(busiestHour) : ''}</span>
                <span className={styles.mutedSmall}>{t('stats.busiest_hour')}</span>
            </div>
            <StatBars
                values={hourHistogram}
                categories={hourCategories}
                tickLabel={(_category, index) => (index % 6 === 0 || index === 23 ? String(index).padStart(2, '0') : '')}
                borderRadius={3}
                height={110}
                categoryGapRatio={0.35}
            />
            {coreHours && (
                <div className={`${styles.mutedSmall} ${styles.coreHoursNote}`}>
                    {t('stats.core_hours', {
                        pct: coreHours.pct,
                        from: formatHour(coreHours.startHour),
                        to: formatHour(coreHours.endHourExclusive)
                    })}
                </div>
            )}
        </div>
    );
};

/** Transits-by-weekday card with busiest-day headline. */
export const WeekdayCard: React.FC<{
    weekdayCounts: number[];
    busiestWeekday: number | null;
    avgTransitsPerDay: number;
}> = ({ weekdayCounts, busiestWeekday, avgTransitsPerDay }) => {
    const { t } = useComponentTranslation();
    const weekdayLetters = useMemo(() => weekdayNames('narrow'), []);
    // Band-scale categories must be unique, so ticks use the letters above
    // while the categories (and tooltips) carry the full names.
    const weekdayCategories = useMemo(() => weekdayNames('long'), []);
    return (
        <div className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.chartHeadline}>
                <span className={styles.chartTitle}>{busiestWeekday !== null ? weekdayCategories[busiestWeekday] : ''}</span>
                <span className={styles.mutedSmall}>
                    {t('stats.busiest_day_sub', { avg: avgTransitsPerDay.toLocaleString(userLocale(), { maximumFractionDigits: 1 }) })}
                </span>
            </div>
            <StatBars
                values={weekdayCounts}
                categories={weekdayCategories}
                tickLabel={(_category, index) => weekdayLetters[index]}
                borderRadius={7}
                height={120}
                categoryGapRatio={0.25}
            />
        </div>
    );
};

/** Transits-per-month card with busiest-month headline (12-month timeline). */
export const MonthlyTransitsCard: React.FC<{ monthly: MonthlyTransits[] }> = ({ monthly }) => {
    const { t } = useComponentTranslation();
    return (
        <div className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.chartHeadline}>
                <span className={styles.chartTitle}>
                    {formatMonth(monthly.reduce((max, entry) => (entry.transits > max.transits ? entry : max)).month, { month: 'long' })}
                </span>
                <span className={styles.mutedSmall}>{t('stats.busiest_month')}</span>
            </div>
            <StatBars
                values={monthly.map((entry) => entry.transits)}
                categories={monthly.map((entry) => formatMonth(entry.month, { month: 'long', year: 'numeric' }))}
                tickLabel={(_category, index) => formatMonth(monthly[index].month, { month: monthly.length > 6 ? 'narrow' : 'short' })}
                borderRadius={7}
                height={120}
                categoryGapRatio={0.25}
            />
        </div>
    );
};

/**
 * Records list: most active day, prey-free streak, then any pre-localized
 * extra rows (household superlatives, device counters).
 */
export const RecordsList: React.FC<{
    mostActiveDay: { day: string; transits: number } | null;
    daysSinceLastPrey: number | null;
    /** Days of history behind the streak: shown as "N+ days" when prey-free throughout. */
    lookbackDays: number;
    extraRows?: { key: string; label: string; value: string }[];
}> = ({ mostActiveDay, daysSinceLastPrey, lookbackDays, extraRows = [] }) => {
    const { t } = useComponentTranslation();
    return (
        <IonList inset={false} className={styles.listCard}>
            {mostActiveDay && (
                <IonItem>
                    <IonLabel>{t('stats.most_active_day')}</IonLabel>
                    <IonText slot="end" color="medium">
                        {formatDay(mostActiveDay.day, { weekday: 'short', day: 'numeric', month: 'short' })}
                        {' · '}{mostActiveDay.transits}
                    </IonText>
                </IonItem>
            )}
            <IonItem lines={extraRows.length === 0 ? 'none' : undefined}>
                <IonLabel>{t('stats.prey_free_streak')}</IonLabel>
                <IonText slot="end" color="medium">
                    {daysSinceLastPrey !== null
                        ? t('stats.days_count', { count: daysSinceLastPrey })
                        : t('stats.days_count_plus', { count: lookbackDays })}
                </IonText>
            </IonItem>
            {extraRows.map((row, index) => (
                <IonItem key={row.key} lines={index === extraRows.length - 1 ? 'none' : undefined}>
                    <IonLabel>{row.label}</IonLabel>
                    <IonText slot="end" color="medium">{row.value}</IonText>
                </IonItem>
            ))}
        </IonList>
    );
};
